import React, { useEffect, useMemo, useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { getBusinessAdmins } from "api/business-user-management";
import {
  IBusinessAdminsResult,
  IGetBusinessAdminsPayload,
} from "api/business-user-management/types";
import { COLORS_MAX_LENGTH_INDEX } from "features/business-compliance/consts";
import { getNextColor } from "features/business-compliance/tab-items";
import { useParams, useSearchParams } from "react-router-dom";
import {
  capitalizeFirstLetter,
  formatDateToDDMMMYYYY,
  getRandomNumber,
} from "utils";
import { START_INDEX } from "utils/constants";

import { customErrorToast } from "ui/custom-toast";
import { DEFAULT_PAGINATION_DATA, Pagination } from "ui/pagination";
import { Status } from "ui/status";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";
import { TSelectedTableRowIds } from "ui/table/types";
import { Heading4, Paragraph4 } from "ui/typography";

import { BusinessAdminsTableHeaderCTAs } from "./BusinessAdminsTableHeaderCTAs";
import { DEFAULT_BUSINESS_ADMINS_PAGINATION_PARAMS, columns } from "./const";

// import { TBusinessAdminRole } from "./types";

/* const adminRoleColorMap: Record<
  TBusinessAdminRole,
  { bg: string; color: string }
> = {
  Owner: {
    bg: "#F4F3FF",
    color: "#5925DC",
  },
  Admin: {
    bg: "#EFF8FF",
    color: "#175CD3",
  },
  Operations: {
    bg: "#F2F4F7",
    color: "#344054",
  },
  Engineering: {
    bg: "#F2F4F7",
    color: "#344054",
  },
}; */

const lastColor: string | null = null;
export const BusinessAdminsTable = () => {
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const search = searchParams.get("search");
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBusinessAdminIds, setSelectedBusinessAdminIds] =
    useState<TSelectedTableRowIds>({});

  const [businessAdminsResult, setBusinessAdminsResult] =
    useState<IBusinessAdminsResult>({
      data: [],
      ...DEFAULT_PAGINATION_DATA,
    });

  const handleGetBusinessAdminsData = async (
    payload: IGetBusinessAdminsPayload
  ) => {
    setIsLoading(true);
    try {
      const { data, error, message } = await getBusinessAdmins(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        setBusinessAdminsResult(data);
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsLoading(false);
  };

  // useEffect(
  //   () => console.log("businessAdminsData: ", businessAdminsData),
  //   [businessAdminsData]
  // );

  const formattedData = useMemo(() => {
    return (businessAdminsResult?.data || []).map((adminData) => {
      const { email, firstName, lastName, status, createdAt, role, id } =
        adminData;
      const bgColor = getNextColor(
        getRandomNumber(START_INDEX, COLORS_MAX_LENGTH_INDEX),
        lastColor
      );

      return {
        "Admin's Name": (
          <Box className="flex items-center gap-3">
            <Box
              className="h-8 w-8 rounded-[50%] flex justify-center items-center"
              css={{
                backgroundColor: bgColor,
                color: "#ffffff",
                flexShrink: 0,
              }}
            >
              <span className="capitalize text-[12.58px] leading-[19.2px] font-medium text-center">
                {firstName?.charAt(START_INDEX)}
              </span>
            </Box>
            <Box>
              <Heading4
                fontSize="14px"
                fontWeight="600"
                lineHeight="20px"
                textColor="#09090B"
              >
                {`${firstName} ${lastName}`}
              </Heading4>
              <Paragraph4
                fontSize="12px"
                lineHeight="16px"
                textColor="#718096"
              >
                {email}
              </Paragraph4>
            </Box>
          </Box>
        ),
        Role: (
          <Status
            customBgColor="#F4F3FF"
            customColor="#5925DC"
            text={capitalizeFirstLetter(role.name)}
            className="w-fit"
          />
        ),
        Status: (
          <Status
            status={status === "active" ? "success" : "error"}
            text={capitalizeFirstLetter(status)}
            className="w-fit"
          />
        ),
        "Date Added": (
          <p className="text-sm text-[#667085]">
            {formatDateToDDMMMYYYY(createdAt)}
          </p>
        ),
        id,
      };
    });
  }, [businessAdminsResult?.data]);
  const { data: _, ...paginationData } = businessAdminsResult;

  useEffect(() => {
    if (!businessId) return;
    const payload: IGetBusinessAdminsPayload = {
      businessId,
      page: Number(page || DEFAULT_BUSINESS_ADMINS_PAGINATION_PARAMS.page),
      limit: Number(limit || DEFAULT_BUSINESS_ADMINS_PAGINATION_PARAMS.limit),
    };

    if (search) {
      payload["search"] = search;
    }
    void handleGetBusinessAdminsData(payload);
  }, [businessId, page, limit, search]);

  // useEffect(
  //   () => console.log("businessAdminsResult: ", businessAdminsResult),
  //   [businessAdminsResult]
  // );

  return (
    <TableWrapper className="!p-0">
      <BusinessAdminsTableHeaderCTAs
      // handleGetBusinessAdminsData={handleGetBusinessAdminsData}
      // businessId={businessId || ""}
      />
      <Table
        columns={columns}
        data={formattedData}
        isLoading={isLoading}
        hasCheckbox
        selectedTableRowIds={selectedBusinessAdminIds}
        setSelectedTableRowIds={setSelectedBusinessAdminIds}
        tableRef={tableRef}
      />
      <Pagination
        paginationData={paginationData}
        data={businessAdminsResult.data}
        defaultPaginationParams={DEFAULT_BUSINESS_ADMINS_PAGINATION_PARAMS}
        tableRef={tableRef}
        isLoadingTable={isLoading}
      />
    </TableWrapper>
  );
};
